import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "components";
import { selectGameId, selectGameObject } from "features/gamestate/gameStateSlice";
import { selectAllQuestionsForRound } from "features/question/questionSlice";
import { GAMESTATE_ROUND_FINAL } from "features/gamestate/constants";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import Provider from "provider";
import logging from "utils/logging";
import {
  clearPanelSelection,
  panelSaving,
  panelSaved,
  panelSaveError,
} from "./editorSlice";
import { isClueWritten } from "./clueWritten";
import "./roundEditor.css";

const log = logging.getLogger("CategoryEditForm");

const CategoryEditForm = ({ categoryIdx, round }) => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const gameObject = useSelector(selectGameObject);
  const questions = useSelector(selectAllQuestionsForRound(round));

  const isFinal = round === GAMESTATE_ROUND_FINAL;
  const cats = Array.isArray(gameObject?.categories?.[round])
    ? gameObject.categories[round]
    : [];
  const currentName = isFinal
    ? gameObject?.categories?.final?.category || ""
    : cats[categoryIdx]?.category || "";

  const [name, setName] = useState(currentName);
  const [intent, setIntent] = useState("keep");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(currentName);
    setIntent("keep");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdx, round]);

  // Count written clues in this column (positional — docs carry no categoryIndex).
  let writtenInCol = 0;
  if (isFinal) {
    if (isClueWritten(questions?.question)) writtenInCol = 1;
  } else {
    for (let row = 1; row <= 5; row += 1) {
      const rowQs = questions?.[row] || [];
      if (isClueWritten(rowQs[categoryIdx])) writtenInCol += 1;
    }
  }

  const showIntentSection = writtenInCol > 0;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    dispatch(panelSaving());
    try {
      if (isFinal) {
        await Provider.updateFinalCategory(gameId, trimmed);
      } else {
        await Provider.updateCategory(gameId, round, categoryIdx, trimmed);
        if (intent === "erase") {
          // The rename already landed; surface an erase-specific failure so the
          // host knows the column was renamed but its clues weren't cleared.
          try {
            await Provider.eraseRoundCategoryClues(gameId, round, categoryIdx);
          } catch (eraseErr) {
            log.error("Category renamed but clearing its clues failed", {
              gameId,
              round,
              categoryIdx,
              err: eraseErr,
            });
            dispatch(
              panelSaveError({
                message: "Renamed, but clearing clues failed — try again",
              })
            );
            setSubmitting(false);
            return;
          }
        }
      }
      // Track only after the write actually succeeds.
      Analytics.track(EVENTS.CATEGORY_RENAMED, {
        game_id: gameId,
        round,
        category_idx: categoryIdx,
        intent,
      });
      dispatch(panelSaved());
      dispatch(clearPanelSelection());
    } catch (err) {
      log.error("Failed to rename category", {
        gameId,
        round,
        categoryIdx,
        intent,
        err,
      });
      dispatch(panelSaveError({ message: "Couldn't rename category — try again" }));
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    dispatch(clearPanelSelection());
    Analytics.track(EVENTS.PANEL_CANCELLED, {
      game_id: gameId,
      round,
      panel_mode: "category",
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
  };

  const plural = writtenInCol !== 1 ? "s" : "";

  return (
    <>
      <div className="re-panel-header">
        <span className="re-panel-header-title">Rename Category</span>
        <button
          type="button"
          className="re-panel-close"
          aria-label="Close editor"
          onClick={handleCancel}
          disabled={submitting}
        >
          ×
        </button>
      </div>

      <div className="re-panel-body">
        {/* Name field */}
        <div className="re-field">
          <label className="re-label">Category name</label>
          <input
            className="re-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Category name"
            disabled={submitting}
            autoFocus
          />
        </div>

        {/* Intent section — only when column has written clues */}
        {showIntentSection && (
          <div className="re-field">
            <p className="re-intent-intro">
              This category has{" "}
              <strong>
                {writtenInCol} written clue{plural}
              </strong>
              . What should we do with them?
            </p>
            <div className="re-intent-cards">
              {/* Keep */}
              <button
                type="button"
                className={`re-intent-card ${intent === "keep" ? "is-selected" : ""}`}
                onClick={() => setIntent("keep")}
                disabled={submitting}
              >
                <span className="re-intent-card-radio" />
                <span>
                  <div className="re-intent-card-title">Keep clues as-is</div>
                  <div className="re-intent-card-desc">
                    Just rename the column. The {writtenInCol} clue{plural} stay; edit
                    individually.
                  </div>
                </span>
              </button>

              {/* Erase */}
              <button
                type="button"
                className={`re-intent-card ${intent === "erase" ? "is-selected" : ""}`}
                onClick={() => setIntent("erase")}
                disabled={submitting}
              >
                <span className="re-intent-card-radio" />
                <span>
                  <div className="re-intent-card-title">Erase all clues</div>
                  <div className="re-intent-card-desc">
                    Delete the {writtenInCol} existing clue{plural} and start with an
                    empty column.
                  </div>
                </span>
              </button>

              {/* AI — disabled with Coming soon tooltip */}
              <Tooltip title="Coming soon — AI-powered clue writing is on the way.">
                <span>
                  <button
                    type="button"
                    className="re-intent-card is-disabled"
                    disabled
                    aria-disabled="true"
                    tabIndex={-1}
                  >
                    <span className="re-intent-card-radio" />
                    <span>
                      <div className="re-intent-card-title">
                        Write fresh clues with AI ✦
                      </div>
                      <div className="re-intent-card-desc">
                        Generate {writtenInCol} new clue{plural} about "
                        {name || currentName}" in seconds.
                      </div>
                    </span>
                  </button>
                </span>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      <div className="re-panel-actions">
        <button
          type="button"
          onClick={handleCancel}
          disabled={submitting}
          style={{
            background: "none",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.5 : 1,
            fontSize: "inherit",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim() || submitting}
          style={{
            background: "var(--color-teal, #2ec4b6)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-md, 8px)",
            padding: "8px 18px",
            cursor: !name.trim() || submitting ? "not-allowed" : "pointer",
            opacity: !name.trim() || submitting ? 0.6 : 1,
            fontWeight: 600,
            fontSize: "inherit",
          }}
        >
          {submitting ? "Renaming…" : "Rename"}
        </button>
      </div>
    </>
  );
};

export default CategoryEditForm;
